// Compact avatar stack — overlapping initials circles, with a +N chip when
// there are more contributors than the visible window. Used on Grid cards
// and the project Drawer "Team" row.

import { avatarColor, initialsFor } from '../../pm/constants';

interface Props {
  /** Emails (or any stable string keys) that map to a deterministic color. */
  emails: string[];
  /** How many circles to show before collapsing to +N. */
  max?: number;
  /** Per-circle diameter in px. */
  size?: number;
}

const AvatarStack: React.FC<Props> = ({ emails, max = 3, size = 22 }) => {
  // De-dupe while preserving order so the first 3 show up consistently.
  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const e of emails) {
    if (!e || seen.has(e)) continue;
    seen.add(e);
    ordered.push(e);
  }
  if (ordered.length === 0) return null;

  const shown = ordered.slice(0, max);
  const extra = ordered.length - shown.length;
  const overlap = Math.round(size * 0.32); // 32% of size — tight overlap

  return (
    <span className="pm-avatar-stack" style={{ display: 'inline-flex' }}>
      {shown.map((email, i) => (
        <span
          key={email}
          className="pm-avatar"
          style={{
            background: avatarColor(email),
            width: size,
            height: size,
            fontSize: Math.round(size * 0.4),
            marginLeft: i === 0 ? 0 : -overlap,
            boxShadow: '0 0 0 2px var(--rr-paper)',
          }}
          title={email}
          aria-hidden="true"
        >
          {initialsFor(email)}
        </span>
      ))}
      {extra > 0 ? (
        <span
          className="pm-avatar pm-avatar-extra"
          style={{
            width: size,
            height: size,
            fontSize: Math.round(size * 0.4),
            marginLeft: -overlap,
            boxShadow: '0 0 0 2px var(--rr-paper)',
          }}
          aria-label={`${extra} more`}
        >
          +{extra}
        </span>
      ) : null}
    </span>
  );
};

export default AvatarStack;
